import { BaseAdapter } from "../base.js";
import type { AdapterContext } from "../base.js";
import type { Url } from "../../data-model/primitives.js";
import type { NovelMetadata, ChapterRef, Chapter } from "../../data-model/novel.js";
import { unsafeBrandAdapterId } from "../../data-model/primitives.js";
import { mapHostname } from "../../utils/url.js";
import type { BrowserPage } from "../../http/browser/types.js";
import { ParseError } from "../../core/errors.js";
import {
  parseNovelPage,
  parseChapterPage,
  parseBookIndexHtml,
  parsePaginationProbe,
} from "./parser.js";

// Walking a TOC longer than one /book/index response requires triggering
// further loadBookIndex calls. Patchright runs `page.evaluate()` in an
// isolated world where the page's globals (loadBookIndex, jQuery, …) are
// not visible, so we don't call loadBookIndex directly. Instead we use the
// pagination DOM as the source of truth and the page's own click delegate
// as our trigger:
//
//   1. The page renders pagination controls under `.pagination` with
//      `<a data-action="loadBookIndex" data-start="..." data-size="...">`
//      links. The "last page" button (`i.fa-angle-double-right`) carries
//      the start index of the final window — total range is therefore
//      known after the first XHR.
//   2. To trigger window N we synthesise an `<a>` element with the same
//      data-attributes, append it, and `.click()` it from the isolated
//      world. The page's main-world delegate (bound to
//      `[data-action="loadBookIndex"]`) picks the event up via DOM
//      bubbling and fires the XHR with a fresh signature.
//
// No magic 500/501 constant; no first-URL clamp guard — the last window's
// `data-start` exposes the exact stop boundary.
const WIKICV_TOC_RESPONSE_TIMEOUT_MS = 30_000;
const WIKICV_INDEX_PATTERN = /\/book\/index\?[^#]*\bbookId=/;
const WIKICV_PROBE_PAGINATION_SCRIPT = `JSON.stringify((() => {
  const last = document.querySelector('.pagination .fa-angle-double-right')?.closest('a');
  if (!last) return null;
  const lastStart = Number(last.getAttribute('data-start'));
  const pageSize = Number(last.getAttribute('data-size'));
  if (!Number.isInteger(lastStart) || lastStart <= 0) return null;
  if (!Number.isInteger(pageSize) || pageSize <= 0) return null;
  return { lastStart, pageSize };
})())`;

export class WikicvAdapter extends BaseAdapter {
  readonly id = unsafeBrandAdapterId("wikicv");
  readonly displayName = "WikiCV (DichTiengHoa)";
  readonly hostnames = [
    "wikicv.net",
    "www.wikicv.net",
    "truyenwikidich.net",
    "www.truyenwikidich.net",
  ];
  override readonly preferredTransport = "browser" as const;

  async fetchMetadata(url: Url, ctx: AdapterContext): Promise<NovelMetadata> {
    const target = this.#normalize(url);
    const $ = await this.loadHtml(target, ctx);
    const meta = parseNovelPage($, target);
    if (meta.status === "unknown") {
      ctx.logger.warn(
        { url: target },
        "wikicv: status resolved to 'unknown' — positional selector div:nth-of-type(4) may have drifted (survey needed)",
      );
    }
    return meta;
  }

  async *fetchChapterList(url: Url, ctx: AdapterContext): AsyncIterable<ChapterRef> {
    if (!ctx.browser) {
      throw new ParseError(
        "wikicv requires the browser transport tier; ensure transport mode is 'auto' or 'browser-required' and patchright/playwright is installed",
        { url },
      );
    }
    const target = this.#normalize(url);
    const page = await ctx.browser.navigate(target, ctx.signal);
    try {
      // Window 0 fires automatically on page load; just await it.
      const first = await page.waitForResponse(WIKICV_INDEX_PATTERN, {
        timeoutMs: WIKICV_TOC_RESPONSE_TIMEOUT_MS,
        signal: ctx.signal,
        parse: "text",
      });
      const firstRefs = parseBookIndexHtml(first.body, target, 0, { logger: ctx.logger });
      if (firstRefs.length === 0) return;
      for (const ref of firstRefs) yield ref;

      const range = await this.#readPaginationRange(page);
      if (!range) return;

      for (let start = range.pageSize; start <= range.lastStart; start += range.pageSize) {
        const responsePromise = page.waitForResponse(WIKICV_INDEX_PATTERN, {
          timeoutMs: WIKICV_TOC_RESPONSE_TIMEOUT_MS,
          signal: ctx.signal,
          parse: "text",
          skipBuffer: true,
        });
        await page.evaluate(this.#triggerLoadScript(start, range.pageSize));
        const captured = await responsePromise;
        const windowRefs = parseBookIndexHtml(captured.body, target, start, { logger: ctx.logger });
        if (windowRefs.length === 0) return;
        for (const ref of windowRefs) yield ref;
      }
    } finally {
      await page.close();
    }
  }

  async #readPaginationRange(
    page: BrowserPage,
  ): Promise<{ lastStart: number; pageSize: number } | null> {
    const raw = await page.evaluate(WIKICV_PROBE_PAGINATION_SCRIPT);
    return parsePaginationProbe(raw);
  }

  // Synthesise the same anchor the page renders for pagination links and
  // click it. The page's main-world delegate handler — bound on a parent
  // element to `[data-action="loadBookIndex"]` — picks the bubbled click
  // event up and fires the signed XHR for this window.
  #triggerLoadScript(start: number, pageSize: number): string {
    return `(() => {
  const a = document.createElement('a');
  a.setAttribute('data-action', 'loadBookIndex');
  a.setAttribute('data-start', ${JSON.stringify(String(start))});
  a.setAttribute('data-size', ${JSON.stringify(String(pageSize))});
  a.setAttribute('href', '#!');
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
})()`;
  }

  async fetchChapter(ref: ChapterRef, ctx: AdapterContext): Promise<Chapter> {
    const $ = await this.loadHtml(ref.url, ctx);
    return parseChapterPage($, ref);
  }

  #normalize(url: Url): Url {
    return mapHostname(url, {
      "truyenwikidich.net": "wikicv.net",
      "www.truyenwikidich.net": "wikicv.net",
      "www.wikicv.net": "wikicv.net",
    });
  }
}
